import { Models } from "appwrite";
import { Link, useNavigate } from "react-router-dom";
import { PostStats } from "@/components/shared";
import { multiFormatDateString } from "@/lib/utils";
import { useUserContext } from "@/context/AuthContext";
import { BadgeCheck, ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useGetUserById } from "@/lib/react-query/queries";

type PostCardProps = {
  post: Models.Document;
  showDealBadge?: boolean;
};

const DealTypeBadge = ({ dealType }: { dealType: string }) => {
  const dealStyles = {
    'physical-goods': 'bg-green-300 text-blue-800',
    'services': 'bg-purple-100 text-purple-800',
    'digital': 'bg-green-100 text-green-800'
  }[dealType] || 'bg-gray-100 text-gray-800';

  const dealLabels = {
    'physical-goods': 'Physical Goods',
    'services': 'Services',
    'digital': 'Digital'
  }[dealType] || dealType;

  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium ${dealStyles}`}>
      {dealLabels}
    </span>
  );
};

const PostCard = ({ post, showDealBadge = false }: PostCardProps) => {
  // ALL HOOKS MUST BE CALLED FIRST - NO EXCEPTIONS
  const { user } = useUserContext();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const postRef = useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const navigate = useNavigate();
  
  // Always call this hook - let the enabled flag handle the conditional logic
  const { data: creator, isLoading: isCreatorLoading } = useGetUserById(post.creatorId || "");

  // CRITICAL: This useEffect must ALWAYS be called, never conditionally
  useEffect(() => {
    // Only run the effect logic if we have the required refs and valid post
    if (!videoRef.current || !postRef.current || !post.creatorId) return;


    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        
        if (entry.isIntersecting) {
          videoRef.current?.play()
            .then(() => setIsPlaying(true))
            .catch(err => console.log("Autoplay prevented:", err));
        } else {
          videoRef.current?.pause();
          setIsPlaying(false);
        }
      },
      { threshold: 0.7 }
    );

    observer.observe(postRef.current);

    return () => {
      observer.disconnect();
    };
  }, [currentImageIndex, post.imageUrl, post.creatorId]); // Include post.creatorId in deps

  // NOW we can do early returns after ALL hooks have been called
  if (!post.creatorId) {
    return null;
  }

  if (isCreatorLoading) {
    return (
      <div className="post-card animate-pulse">
        <div className="flex-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-dark-4 rounded-full"></div>
            <div className="flex flex-col gap-2">
              <div className="h-4 bg-dark-4 rounded w-32"></div>
              <div className="h-3 bg-dark-4 rounded w-24"></div>
            </div>
          </div>
        </div>
        <div className="h-4 bg-dark-4 rounded mb-2"></div>
        <div className="h-4 bg-dark-4 rounded w-3/4 mb-4"></div>
        <div className="h-64 bg-dark-4 rounded-xl"></div>
      </div>
    );
  }

  const images = typeof post.imageUrl === 'string' 
    ? [post.imageUrl] 
    : post.imageUrl || [];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleVideoClick = (e: React.MouseEvent) => {
    if (!(e.target as HTMLElement).closest('button')) {
      navigate(`/posts/${post.$id}`);
    }
  };

  const mediaFiles = typeof post.imageUrl === 'string'
    ? [post.imageUrl]
    : Array.isArray(post.imageUrl) ? post.imageUrl : [];

  const toggleMute = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(err => console.log("Play failed:", err));
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <div className="post-card" ref={postRef}>
      <div className="flex-between">
        <div className="flex items-center gap-3">
          <Link to={`/profile/${post.creatorId}`}>
            <img
              src={creator?.imageUrl || "/assets/icons/profile-placeholder.svg"}
              alt="creator"
              className="w-12 h-12 rounded-full"
            />
          </Link>

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <p className="base-medium lg:body-bold text-light-1">
                {creator?.name}
                {creator?.isKYDone && (
                  <BadgeCheck className="inline ml-1 text-primary-500" size={16} />
                )}
              </p>
               {showDealBadge && post.category && (
                <DealTypeBadge dealType={post.category} />
              )}
            </div>
            <div className="flex items-center gap-2 text-light-3">
              <p className="subtle-semibold lg:small-regular">{multiFormatDateString(post.$createdAt)}</p>
              {post.location && (
                <>
                  <span className="mx-2">•</span>
                  <p className="subtle-semibold lg:small-regular">{post.location}</p>
                </>
              )}
            </div>
          </div>
        </div>

        <Link to={`/update-post/${post.$id}`} className={`${user.id !== post.creatorId && "hidden"}`}>
          <img src={"/assets/icons/edit.svg"} alt="edit" width={20} height={20} />
        </Link>
      </div>

      <Link to={`/posts/${post.$id}`}>
        <div className="small-medium lg:base-medium py-5">
          <p className="whitespace-pre-wrap">{post.caption?.length > 250 ? `${post.caption.substring(0, 250)}...` : post.caption}</p>
          <ul className="flex flex-wrap gap-2 mt-2">
            {post.tags?.length > 0 && post.tags.map((tag: string, index: number) => (
              <li key={`${tag}-${index}`} className="text-light-3 small-regular inline-block">
                {tag.trim() && <span>#{tag.trim()}</span>}
              </li>
            ))}
          </ul>
        {post.isSellingPost && (
          <div className="relative">
            <div 
              className="mt-3 bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-md inline-flex items-center gap-2 hover:bg-emerald-100 transition-colors cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const tooltip = e.currentTarget.nextElementSibling;
                tooltip?.classList.toggle('hidden');
              }}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                className="h-5 w-5 bg-gradient-to-br from-emerald-400 to-blue-500 text-white p-0.5 rounded-full flex-shrink-0"
                aria-hidden="true"
              >
                <path fill="currentColor" fillRule="evenodd" d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12c5.16-1.26 9-6.45 9-12V5l-9-4zm-1 17.93V12H5V6.3l6-2.67V18.93z" clipRule="evenodd"/>
              </svg>

              <div className="flex flex-col sm:flex-row sm:items-baseline gap-1">
                <div className="flex items-center gap-1">
                  <span className="text-emerald-800 font-semibold text-sm">
                    Protected Payment
                  </span>
                </div>
                <span className="text-xs text-emerald-600">
                  (Recommended)
                </span>
              </div>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24"
                className="h-4 w-4 text-emerald-500 ml-auto"
                aria-hidden="true"
              >
                <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
            </div>

            <div 
              className="absolute hidden z-50 w-full mt-1 p-3 text-sm bg-white border border-gray-200 rounded-lg shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-1">
                <div className="font-medium text-gray-900">Secure Transaction</div>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.closest('.absolute')?.classList.add('hidden');
                  }}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label="Close tooltip"
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                  </svg>
                </button>
              </div>
              <ul className="space-y-1 text-gray-600">
                <li className="flex items-start">
                  <svg className="h-4 w-4 text-emerald-500 mt-0.5 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  Funds held securely until delivery
                </li>
                <li className="flex items-start">
                  <svg className="h-4 w-4 text-emerald-500 mt-0.5 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  Fraud protection for both parties
                </li>
                <li className="flex items-start">
                  <svg className="h-4 w-4 text-emerald-500 mt-0.5 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  Faster payout upon confirmation
                </li>
              </ul>
            </div>
          </div>
        )}
        </div>

        {mediaFiles.length > 0 && (
          <div className="relative w-full rounded-xl overflow-hidden">
            {mediaFiles.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.preventDefault(); prevImage(); }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 p-2 rounded-full z-10 hover:bg-black/70 transition-colors"
                >
                  <ChevronLeft color="white" size={20} />
                </button>
                <button
                  onClick={(e) => { e.preventDefault(); nextImage(); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 p-2 rounded-full z-10 hover:bg-black/70 transition-colors"
                >
                  <ChevronRight color="white" size={20} />
                </button>
              </>
            )}

            {(() => {
              const currentMedia = mediaFiles[currentImageIndex];
              const isVideo = currentMedia?.includes('&type=video') || currentMedia?.includes('/view?');
              
              return isVideo ? (
                <div className="relative w-full h-full" onClick={handleVideoClick}>
                  <video
                    ref={videoRef}
                    src={currentMedia}
                    playsInline
                    loop
                    muted={isMuted}
                    preload="metadata"
                    disablePictureInPicture
                    controlsList="nodownload nofullscreen noremoteplayback"
                    className="w-full h-auto max-h-[450px] object-contain rounded-xl"
                    poster="/assets/icons/video-placeholder.png"
                    onError={(e) => console.error("Video error:", e)}
                    onClick={togglePlay}
                  />
                  <button
                    onClick={toggleMute}
                    className="absolute bottom-4 right-4 bg-black/50 p-2 rounded-full z-10 hover:bg-black/70 transition-colors"
                  >
                    {isMuted ? (
                      <VolumeX color="white" size={20} />
                    ) : (
                      <Volume2 color="white" size={20} />
                    )}
                  </button>
                </div>
              ) : (
                <img
                  src={currentMedia}
                  alt="post media"
                  className="w-full h-auto max-h-[450px] object-contain rounded-xl"
                />
              );
            })()}
          </div>
        )}
      </Link>

      <PostStats post={post} userId={user.id} postCreatorId={post.creatorId}/>
    </div>
  );
};

export default PostCard;